<script lang="ts" module>
  import eye from '../../../../assets/imgs/character/eye.svg';
  import glasses from '../../../../assets/imgs/character/glasses.svg';
  import gradient from '../../../../assets/imgs/character/gradient.jpeg';
  import mouth from '../../../../assets/imgs/character/mouth.svg';
  import shadow from '../../../../assets/imgs/character/shadow.svg';
  import star from '../../../../assets/imgs/character/star.svg';
  import { directionConfigs, rightConfig, sizes, type PositionConfig } from './teo-ball-positions';

  export type TeoBallVariant = 'idle-right' | 'idle-left' | 'idle-straight';
</script>

<script lang="ts">
  let {
    variant = 'idle-right',
    transitionDuration = '0.6s',
    class: className,
    ...props
  }: {
    variant?: TeoBallVariant;
    transitionDuration?: string;
    class?: string;
  } & Record<string, unknown> = $props();

  let direction = $derived(variant?.split('-').slice(1).join('-') ?? 'right');
  let p: PositionConfig = $derived(directionConfigs[direction] ?? rightConfig);
</script>

<div class="relative aspect-square {className}" style:--td={transitionDuration} {...props}>
  <div class="absolute inset-0">
    <img
      src={star}
      class="float-p1 transition-pos absolute"
      style:left="{p.star1Left}%"
      style:top="{p.star1Top}%"
      style:width="{sizes.star1.width}%"
      alt=""
    />
    <img
      src={star}
      class="float-p2 transition-pos absolute"
      style:left="{p.star2Left}%"
      style:top="{p.star2Top}%"
      style:width="{sizes.star2.width}%"
      alt=""
    />
    <img
      src={gradient}
      class="float-p3 transition-pos absolute rounded-full"
      style:left="{p.particle1Left}%"
      style:top="{p.particle1Top}%"
      style:width="{sizes.particle1.width}%"
      alt=""
    />
    <img
      src={gradient}
      class="float-p4 transition-pos absolute rounded-full"
      style:left="{p.particle2Left}%"
      style:top="{p.particle2Top}%"
      style:width="{sizes.particle2.width}%"
      alt=""
    />
  </div>
  <div
    class="transition-pos absolute"
    style:left="{p.bodyLeft}%"
    style:top="{p.bodyTop}%"
    style:width="{sizes.body.width}%"
    style:height="{sizes.body.height}%"
  >
    <img
      src={shadow}
      class="transition-pos absolute bottom-0"
      style:left="{p.shadowLeft}%"
      style:width="{sizes.shadow.width}%"
      alt=""
    />
    <div class="float-face absolute inset-0">
      <img src={gradient} class="w-full rounded-full" alt="" />
      <img
        src={eye}
        class="transition-pos absolute"
        style:left="{p.eyeLLeft}%"
        style:top="{p.eyeLTop}%"
        style:width="{sizes.eye.width}%"
        alt=""
      />
      <img
        src={eye}
        class="transition-pos absolute"
        style:left="{p.eyeRLeft}%"
        style:top="{p.eyeRTop}%"
        style:width="{sizes.eye.width}%"
        alt=""
      />
      <img
        src={mouth}
        class="transition-pos absolute"
        style:left="{p.mouthLeft}%"
        style:top="{p.mouthTop}%"
        style:width="{sizes.mouth.width}%"
        alt=""
      />
    </div>
    <img
      src={glasses}
      class="float-glasses transition-pos absolute"
      style:left="{p.glassesLeft}%"
      style:top="{p.glassesTop}%"
      style:width="{sizes.glasses.width}%"
      alt=""
    />
  </div>
</div>

<style>
  .transition-pos {
    transition:
      left var(--td) ease-in-out,
      top var(--td) ease-in-out;
  }

  @keyframes float {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(var(--float-y));
    }
  }

  @keyframes float-xy {
    0%,
    100% {
      transform: translate(0, var(--float-y));
    }
    25% {
      transform: translate(var(--float-x), 0);
    }
    50% {
      transform: translate(0, calc(var(--float-y) * -1));
    }
    75% {
      transform: translate(calc(var(--float-x) * -1));
    }
  }

  .float-face {
    --float-y: -3%;
    animation: float 9s ease-in-out infinite;
  }

  .float-glasses {
    --float-y: -10%;
    animation: float 9s ease-in-out 1s infinite;
  }

  .float-p1 {
    --float-x: 6%;
    --float-y: -8%;
    animation: float-xy 15s ease-in-out infinite;
  }

  .float-p2 {
    --float-x: -5%;
    --float-y: 16%;
    animation: float-xy 16s ease-in-out 0.5s infinite;
  }

  .float-p3 {
    --float-x: -7%;
    --float-y: -15%;
    animation: float-xy 20s ease-in-out 1s infinite;
  }

  .float-p4 {
    --float-x: 5%;
    --float-y: 17%;
    animation: float-xy 17s ease-in-out 0.3s infinite;
  }
</style>
